import React, { useState, useEffect, useCallback } from 'react';
import { useT } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../components/ui/use-toast';
import { confirm } from '../../components/ui/confirm-dialog';
import { customerAdminService } from '../../services/customerService';
import { accountsService } from '../../services/accountsService';
import { useRestaurant } from '../../context/RestaurantContext';
import { Users, Search, Plus, Eye, Pencil, Trash2, Mail, Phone, X } from 'lucide-react';
import { sanitizeMuLocal, toMuPhone, fromMuPhone, isCompleteMuLocal } from '../../lib/muPhone';

// The full project-wide list (matches Customer/RestaurantStaff SALUTATIONS).
const SALUTATIONS = [
  ['', 'None'],
  ['MR', 'Mr'],
  ['MRS', 'Mrs'],
  ['MS', 'Ms'],
  ['MISS', 'Miss'],
  ['DR', 'Dr'],
  ['PROF', 'Prof'],
];

const PAGE_SIZE = 20;

const emptyForm = {
  type: 'customer',       // 'customer' | 'user' — chooses which account to create
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  username: '',           // user (staff) only
  staff_type_id: '',      // user (staff) only — role
  is_active: true,
};

export default function CustomersPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { restaurantId } = useRestaurant();
  const [roles, setRoles] = useState([]);

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // A server-side sort key, so page 2 continues page 1's order instead of re-sorting 20 loose rows.
  const [order, setOrder] = useState('name');
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);

  // Edit modal — customers only (staff are edited under Staff → All Staff).
  const emptyEdit = { id: null, salutation: '', first_name: '', last_name: '', email: '', phone: '', is_active: true, birthday: '', whatsapp_opt_in: false };
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customerAdminService.list({ search, page, pageSize: PAGE_SIZE, order });
      setCustomers(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to load customers.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, page, order, toast]);

  // Click a column to sort ascending, again to flip, returning to page 1 either way.
  const sortBy = (key) => {
    setOrder((cur) => (cur === key ? `-${key}` : key));
    setPage(1);
  };
  const sortMark = (key) =>
    (order === key ? ' ↑' : order === `-${key}` ? ' ↓' : '');

  useEffect(() => {
    load();
  }, [load]);

  // Roles for the "User (staff)" create option.
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || '/api';
    fetch(`${API_URL}/users/login/roles/`)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d?.data) ? d.data : d?.data?.roles || [];
        setRoles(list);
      })
      .catch(() => setRoles([]));
  }, []);

  // Debounce the search box — reset to page 1 whenever the term changes.
  const onSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      if (form.type === 'user') {
        await accountsService.create({
          type: 'user',
          restaurant_id: restaurantId,
          salutation: form.salutation,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          username: form.username,
          phone: toMuPhone(form.phone),
          password: form.password,
          staff_type_id: form.staff_type_id ? Number(form.staff_type_id) : undefined,
          is_active: form.is_active,
        });
        toast({
          title: 'Staff user created',
          description: `${form.first_name} was added. Manage staff under Staff → All Staff.`,
        });
        setCreateOpen(false);
        setForm(emptyForm);
        return; // staff users don't show in the customer list
      }

      await accountsService.create({
        type: 'customer',
        salutation: form.salutation,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: toMuPhone(form.phone),
        password: form.password,
        is_active: form.is_active,
      });
      toast({ title: 'Customer created', description: `${form.first_name} was added.` });
      setCreateOpen(false);
      setForm(emptyForm);
      setPage(1);
      load();
    } catch (err) {
      toast({
        title: form.type === 'user' ? 'Could not create user' : 'Could not create customer',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (id) => {
    try {
      const data = await customerAdminService.get(id);
      setViewing(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const openEdit = async (id) => {
    try {
      const data = await customerAdminService.get(id);
      setEditForm({
        id: data.id,
        salutation: data.salutation || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: fromMuPhone(data.phone),
        is_active: data.is_active !== false,
        birthday: data.birthday || '',
        whatsapp_opt_in: !!data.whatsapp_opt_in,
      });
      setEditOpen(true);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleUpdate = async () => {
    setSavingEdit(true);
    try {
      await customerAdminService.update(editForm.id, {
        salutation: editForm.salutation,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: toMuPhone(editForm.phone),
        is_active: editForm.is_active,
        birthday: editForm.birthday || '',
        whatsapp_opt_in: !!editForm.whatsapp_opt_in,
      });
      toast({ title: 'Customer updated', description: `${editForm.first_name} was saved.` });
      setEditOpen(false);
      setEditForm(emptyEdit);
      load();
    } catch (err) {
      toast({ title: 'Could not update customer', description: err.message, variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (customer) => {
    const ok = await confirm({
      title: 'Delete customer?',
      description: `${customer.full_name || customer.email} will be permanently removed.`,
      confirmText: 'Delete',
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await customerAdminService.remove(customer.id);
      toast({ title: 'Customer deleted' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <Users className="h-7 w-7 text-amber-600" />
            Customers
          </h1>
          <p className="text-gray-600 mt-1">{total} registered customer{total === 1 ? '' : 's'}</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus size={16} className="mr-1" /> Add Account
        </Button>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email or phone…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading customers…</div>
        ) : customers.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            {search ? 'No customers match your search.' : 'No customers yet. Add your first one.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {/* Sortable columns; the email column came off, since OTP guests carry a placeholder address. */}
                  {[['name', 'Name'], ['phone', 'Phone'], ['status', 'Status'],
                    ['last_activity', 'Last Activity'], ['created', 'Date Created']].map(([key, label]) => (
                    <th key={key} className="text-left font-medium px-4 py-3">
                      <button
                        type="button"
                        onClick={() => sortBy(key)}
                        className="hover:text-gray-900 select-none"
                        title={`Sort by ${label.toLowerCase()}`}
                      >
                        {label}{sortMark(key)}
                      </button>
                    </th>
                  ))}
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customers.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    {/* The name opens the guest's CRM 360 - the same profile the reservation screens link to. */}
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to={`/crm?customer=${c.id}&from=customers`}
                        className="text-primary hover:underline underline-offset-2"
                        title="Open the full customer profile — bookings, orders, favourites"
                      >
                        {c.full_name || '—'}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {/* The newest sign of life - sign-in, order or booking - computed server-side, so the column really sorts. */}
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {c.last_activity_at
                        ? new Date(c.last_activity_at).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-400">no activity yet</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {c.created_at
                        ? new Date(c.created_at).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleView(c.id)}>
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/customers/${c.id}`)}>{t('crm.p.profileBtn')}</Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(c.id)}>
                          <Pencil size={14} className="mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(c)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Account type — one form creates a customer or a staff user. */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Account Type *</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'customer', label: 'Customer' },
                  { key: 'user', label: 'User (Staff)' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setForm({ ...form, type: opt.key })}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      form.type === opt.key
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-[90px_1fr_1fr] gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <select
                  value={form.salutation}
                  onChange={(e) => setForm({ ...form, salutation: e.target.value })}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                >
                  {SALUTATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone * (unique — 8 digits)</label>
              <Input placeholder="XXXX XXXX" maxLength={8} inputMode="numeric"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: sanitizeMuLocal(e.target.value) })} />
            </div>
            {form.type === 'user' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Username *</label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={form.staff_type_id}
                    onChange={(e) => setForm({ ...form, staff_type_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select role…</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Password * (min 6 chars)</label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={
                saving ||
                !form.first_name ||
                !form.email ||
                !isCompleteMuLocal(form.phone) ||
                form.password.length < 6 ||
                (form.type === 'user' && (!form.last_name || !form.username || !form.staff_type_id))
              }
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {saving ? 'Saving…' : form.type === 'user' ? 'Create User' : 'Create Customer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View modal */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{viewing?.full_name || 'Customer'}</span>
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 pt-2 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Mail size={15} className="text-gray-400" />
                {viewing.email_is_placeholder
                  ? <span className="text-gray-400 italic">no email on file</span>
                  : viewing.email}
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Phone size={15} className="text-gray-400" /> {viewing.phone || '—'}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Status:</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  viewing.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {viewing.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {viewing.last_login_at && (
                <div className="text-gray-500">Last login: {new Date(viewing.last_login_at).toLocaleString()}</div>
              )}
              {viewing.created_at && (
                <div className="text-gray-500">Joined: {new Date(viewing.created_at).toLocaleDateString()}</div>
              )}
            </div>
          )}
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setViewing(null)}>
              <X size={14} className="mr-1" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit modal — customers only */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* The same three-up row as the create form, so a guest recorded without a title can be given one. */}
            <div className="grid grid-cols-[90px_1fr_1fr] gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <select
                  value={editForm.salutation}
                  onChange={(e) => setEditForm({ ...editForm, salutation: e.target.value })}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                >
                  {SALUTATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone * (unique — 8 digits)</label>
              <Input placeholder="XXXX XXXX" maxLength={8} inputMode="numeric"
                value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: sanitizeMuLocal(e.target.value) })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('crm.p.birthdayLabel')}</label>
              <Input type="date" value={editForm.birthday} onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!editForm.whatsapp_opt_in} onChange={(e) => setEditForm({ ...editForm, whatsapp_opt_in: e.target.checked })} />
              {t('crm.p.whatsappLabel')}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdate}
              disabled={savingEdit || !editForm.first_name || !editForm.email || !isCompleteMuLocal(editForm.phone)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {savingEdit ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
